import type { ReactElement } from 'react'
import { useTranslate } from '../lib/i18n-provider'
import type { Stay } from '../lib/availability-client'

/**
 * Eén maand van de agenda van een woning.
 *
 * Wat een verhuurder aan een agenda vraagt is één ding: welke dagen zijn vrij. Al
 * het andere is bijzaak, en daarom wint de kleur van de dag het hier van de
 * details van de periode.
 *
 * Drie beslissingen die het gedrag bepalen:
 *
 *  1. **Half-open, net als in de database.** Een verblijf van 3 tot 5 juli bezet 3
 *     en 4, niet 5. De vertrekdag is weer boekbaar, en een agenda die dat anders
 *     tekent laat een nacht per boeking onverkoopbaar liggen.
 *
 *  2. **Bezet wint van geblokkeerd.** Een dag krijgt de zwaarste toestand die erop
 *     rust. Bij een reservering die tegen een blokkade aan ligt, is "er ligt een
 *     boeking" wat de verhuurder moet zien.
 *
 *  3. **Geen tabel.** Een maandraster is geen data-tabel maar een lijst dagen met
 *     een toestand; met `role="grid"` en een cel per dag is hij met toetsenbord en
 *     schermlezer te doorlopen zonder dat er kolomkoppen omheen moeten die niets
 *     betekenen.
 */

export type DayState = 'free' | 'booked' | 'pending' | 'blocked' | 'channel'

/**
 * Eén dag in de agenda.
 *
 * `state` gaat over de NACHT van deze dag: de nacht van 3 op 4 juli hoort bij 3 juli.
 * `arrival` en `departure` gaan over de DAG, en die twee zijn nodig omdat een dag in
 * tweeën kan vallen — 's ochtends vertrekt de een, 's middags komt de ander.
 *
 * Dat onderscheid is niet cosmetisch. Een vertrekdag zonder nieuwe boeking is
 * volledig ingekleurd nooit juist: die middag is te huur. Hem als bezet tekenen laat
 * een verhuurder denken dat de dag vol zit, en een gast dat hij niet kan komen —
 * terwijl de boekingsmotor die reservering gewoon aanneemt (half-open interval,
 * §17.6).
 */
export interface CalendarDay {
  readonly date: string
  /** De toestand van de nacht die op deze dag begint. */
  readonly state: DayState
  /** Er komt iemand aan op deze dag: de ochtend is nog vrij. */
  readonly arrival: boolean
  /** Er vertrekt iemand op deze dag: de middag is weer vrij, tenzij `state` bezet is. */
  readonly departure: boolean
}

export interface MonthCalendarProps {
  /** Eerste dag van de maand, `YYYY-MM-DD`. */
  readonly from: string
  readonly stays: readonly Stay[]
  /** Ondersteunt zowel de UI-taal als de weekstart van de gebruiker. */
  readonly locale: string
}

/** Zwaarte-volgorde; hoger wint bij overlap. */
const WEIGHT: Readonly<Record<DayState, number>> = {
  free: 0,
  blocked: 1,
  pending: 2,
  channel: 3,
  booked: 4,
}

/** De toestand die één periode aan een dag geeft. */
export function stateOf(stay: Stay): DayState {
  if (stay.kind === 'owner_block' || stay.kind === 'maintenance') {
    return 'blocked'
  }
  if (stay.kind === 'channel') {
    return 'channel'
  }
  return stay.status === 'hold' ? 'pending' : 'booked'
}

/** Aantal dagen in de maand die op `from` begint. */
export function daysInMonth(from: string): number {
  const start = new Date(`${from}T00:00:00Z`)
  const next = new Date(start)
  next.setUTCMonth(next.getUTCMonth() + 1)
  return Math.round((next.getTime() - start.getTime()) / 86_400_000)
}

/** `YYYY-MM-DD` van dag `index` (0-gebaseerd) in de maand die op `from` begint. */
export function dayAt(from: string, index: number): string {
  const moment = new Date(`${from}T00:00:00Z`)
  moment.setUTCDate(moment.getUTCDate() + index)
  return moment.toISOString().slice(0, 10)
}

/**
 * De toestand per dag van de maand.
 *
 * Pure functie en los van React, zodat de half-open grens toetsbaar is zonder een
 * scherm: dat is precies het soort regel dat stilzwijgend één dag verschuift.
 */
export function monthStates(from: string, stays: readonly Stay[]): readonly DayState[] {
  return monthDays(from, stays).map((day) => day.state)
}

/**
 * De dagen van de maand, met per dag de nacht én de aankomsten en vertrekken.
 *
 * Pure functie en los van React, zodat de half-open grens toetsbaar is zonder een
 * scherm: dat is precies het soort regel dat stilzwijgend één dag verschuift.
 */
export function monthDays(from: string, stays: readonly Stay[]): readonly CalendarDay[] {
  const total = daysInMonth(from)
  const states: DayState[] = Array.from({ length: total }, () => 'free')
  const arrivals = new Set<string>()
  const departures = new Set<string>()

  for (const stay of stays) {
    const state = stateOf(stay)
    arrivals.add(stay.checkIn)
    departures.add(stay.checkOut)

    for (let index = 0; index < total; index += 1) {
      const day = dayAt(from, index)
      // `>= checkIn` en `< checkOut`: de vertrekdag heeft geen nacht.
      if (day >= stay.checkIn && day < stay.checkOut) {
        const current = states[index] ?? 'free'
        if (WEIGHT[state] > WEIGHT[current]) {
          states[index] = state
        }
      }
    }
  }

  return states.map((state, index) => {
    const date = dayAt(from, index)
    return {
      date,
      state,
      arrival: arrivals.has(date),
      departure: departures.has(date),
    }
  })
}

export function MonthCalendar({ from, stays, locale }: MonthCalendarProps): ReactElement {
  const t = useTranslate()
  const days = monthDays(from, stays)

  const weekdayNames = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
  const first = new Date(`${from}T00:00:00Z`)
  // Maandag als eerste kolom. `getUTCDay()` geeft zondag als 0; die hoort achteraan.
  const leading = (first.getUTCDay() + 6) % 7

  return (
    <div className="month" role="grid" aria-label={t('dashboard.availability.calendar.label')}>
      <div className="month__head" role="row">
        {Array.from({ length: 7 }, (_unused, index) => {
          // 2024-01-01 was een maandag; alleen gebruikt om de dagnamen op te halen.
          const sample = new Date(Date.UTC(2024, 0, 1 + index))
          return (
            <span className="month__weekday" key={index} role="columnheader">
              {weekdayNames.format(sample)}
            </span>
          )
        })}
      </div>

      <div className="month__grid" role="row">
        {Array.from({ length: leading }, (_unused, index) => (
          <span aria-hidden="true" className="month__pad" key={`pad-${String(index)}`} />
        ))}
        {days.map((day, index) => (
          <span
            aria-label={`${day.date} — ${t(dayLabelKey(day))}`}
            className="month__day"
            /* De halve dag zit in de opmaak: `arrival` kleurt de middag,
               `departure` de ochtend. Een vertrekdag zonder opvolger is dus half
               ingekleurd, want die middag is te huur. */
            data-arrival={day.arrival ? 'true' : undefined}
            data-departure={day.departure ? 'true' : undefined}
            data-state={day.state}
            key={day.date}
            role="gridcell"
          >
            <span className="month__number">{index + 1}</span>
          </span>
        ))}
      </div>

      {/* Zonder legenda is een gekleurd vlak alleen kleur. */}
      <ul className="month__legend">
        {(['free', 'pending', 'booked', 'channel', 'blocked'] as const).map((state) => (
          <li className="month__legendItem" key={state}>
            <span aria-hidden="true" className="month__swatch" data-state={state} />
            {t(`dashboard.availability.state.${state}`)}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Wat er over deze dag wordt voorgelezen.
 *
 * De halve dag hoort óók in het label en niet alleen in de kleur: een schermlezer
 * krijgt anders "bezet" te horen op een dag waarvan de middag vrij is (§17.30).
 */
function dayLabelKey(day: CalendarDay): string {
  if (day.state === 'free' && day.departure) {
    return 'dashboard.availability.state.departureOnly'
  }
  if (day.state !== 'free' && day.arrival && day.departure) {
    return 'dashboard.availability.state.turnover'
  }
  if (day.state !== 'free' && day.arrival) {
    return 'dashboard.availability.state.arrival'
  }
  return `dashboard.availability.state.${day.state}`
}
